import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CssBaseline
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TimerIcon from '@mui/icons-material/Timer';
import logo from '../assets/logo.png';

// Tema específico para a landing page
const landingTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B365D',
    },
    secondary: {
      main: '#78B6CB',
    },
    background: {
      default: '#ffffff',
      paper: '#f5f5f5',
    },
  },
});

export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Seção 1 - Hero */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 6, md: 10 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src={logo} 
                alt="Habitop Logo" 
                style={{ 
                  height: '100px',
                  width: 'auto',
                  marginBottom: '2rem',
                  filter: 'brightness(0) invert(1)'
                }} 
              />
            </Box>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h1"
              fontWeight="bold"
              gutterBottom
              sx={{ textAlign: 'center', mb: 2 }}
            >
              Seu sistema para organizar hábitos, metas e rotina em um só lugar
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                maxWidth: '700px',
                mx: 'auto',
                opacity: 0.95
              }}
            >
              Transforme sua rotina em um sistema organizado, visual e motivador. 
              Organize seus hábitos, metas e rotina em um só lugar e comece a ver progresso diário em até 7 dias de uso consistente.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                  }
                }}
              >
                Começar agora
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Chip
                label="Preço promocional de lançamento"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  py: 2.5
                }}
              />
            </Box>
          </Container>
        </Box>

        {/* Seção 2 - Para quem é a Habitop */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{ mb: 4, color: 'primary.main' }}
          >
            Para quem é a Habitop?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  borderTop: '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Pessoas que sentem que o dia "escapa"
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Se você vive sentindo que não tem controle da sua rotina e quer uma forma visual e organizada de acompanhar seus hábitos diários.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  borderTop: '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Quem já tentou apps de to-do list
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Se você já experimentou planners ou apps de tarefas, mas não conseguiu manter o hábito de usar. A Habitop foi feita para ser usada todos os dias.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  borderTop: '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Quem quer ver progresso real
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Se você quer tirar metas da cabeça e ver o progresso de verdade, com estatísticas visuais e acompanhamento de dias perfeitos.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Seção 3 - O que a Habitop faz */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h2"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ mb: 6, color: 'primary.main' }}
            >
              O que a Habitop faz
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <CalendarTodayIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
                  Organize sua rotina diária
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Crie hábitos, acompanhe dias perfeitos e receba alertas visuais que te ajudam a manter a consistência no dia a dia.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <EmojiEventsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
                  Tire metas do papel
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Transforme suas metas em tarefas concretas, acompanhe o progresso e complete desafios que te motivam a continuar.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <TimerIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
                  Foque no que importa
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Use o Pomodoro para manter o foco, organize notas importantes e visualize seu calendário de forma clara e objetiva.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Seção 4 - Antes x Depois */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, color: 'primary.main' }}
          >
            Antes x Depois
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: '#ffebee',
                  borderLeft: '4px solid',
                  borderColor: 'error.main'
                }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom color="error">
                  Antes
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Listas espalhadas em vários lugares
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Esquece de manter hábitos importantes
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Metas sem acompanhamento real
                  </Typography>
                  <Typography component="li" variant="body1">
                    Sensação de que o dia "passou voando"
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: '#e8f5e9',
                  borderLeft: '4px solid',
                  borderColor: 'success.main'
                }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom color="success.main">
                  Depois
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Visão clara do seu dia e semana
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Lembretes visuais que te ajudam a manter consistência
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    Acompanhamento real de progresso
                  </Typography>
                  <Typography component="li" variant="body1">
                    Sensação de controle e avanço constante
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Seção 5 - Planos e Preços */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h2"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ mb: 2, color: 'primary.main' }}
            >
              Planos e Preços
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Preço promocional de lançamento. Quem entrar agora mantém o valor.
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {/* Plano Básico */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    border: '2px solid',
                    borderColor: 'divider',
                    position: 'relative'
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Plano Básico
                  </Typography>
                  <Box sx={{ my: 3 }}>
                    <Typography variant="h3" component="span" fontWeight="bold" color="primary.main">
                      R$ 15,90
                    </Typography>
                    <Typography variant="body1" component="span" color="text.secondary">
                      /mês
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    O suficiente para começar
                  </Typography>
                  <Box sx={{ textAlign: 'left', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Hábitos e calendário</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Metas e tarefas</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Notas importantes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Pomodoro e Player</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Backup em nuvem</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Até 5 hábitos, 3 metas</Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: 'primary.main',
                        color: 'white'
                      }
                    }}
                  >
                    Começar agora
                  </Button>
                </Paper>
              </Grid>

              {/* Plano PRO */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    border: '3px solid',
                    borderColor: 'primary.main',
                    position: 'relative',
                    bgcolor: 'primary.main',
                    color: 'white'
                  }}
                >
                  <Chip
                    label="MAIS POPULAR"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      bgcolor: 'secondary.main',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Plano PRO
                  </Typography>
                  <Box sx={{ my: 3 }}>
                    <Typography variant="h3" component="span" fontWeight="bold">
                      R$ 25,90
                    </Typography>
                    <Typography variant="body1" component="span" sx={{ opacity: 0.9 }}>
                      /mês
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                    Tudo + recursos avançados
                  </Typography>
                  <Box sx={{ textAlign: 'left', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Tudo do Plano Básico</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Desafios ilimitados</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Estatísticas detalhadas</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Personalização visual</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Exportação de dados</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ color: 'white', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Hábitos, metas e desafios ilimitados</Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                      }
                    }}
                  >
                    Começar agora
                  </Button>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Cancele quando quiser. Sem compromisso.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Seção 6 - FAQ */}
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, color: 'primary.main' }}
          >
            Perguntas Frequentes
          </Typography>
          <Box>
            <Accordion expanded={expanded === 'faq1'} onChange={handleAccordionChange('faq1')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Preciso de cartão de crédito para começar?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Sim, é necessário um cartão de crédito para assinar os planos Básico ou PRO. 
                  Mas você pode cancelar a qualquer momento, sem compromisso.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'faq2'} onChange={handleAccordionChange('faq2')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Posso usar em mais de um dispositivo?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Sim! A Habitop funciona em qualquer dispositivo com navegador (computador, tablet, celular). 
                  Seus dados ficam sincronizados na nuvem, então você pode acessar de onde quiser.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'faq3'} onChange={handleAccordionChange('faq3')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Consigo exportar meus dados?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Sim, no plano PRO você pode exportar seus dados a qualquer momento. 
                  No plano Básico, essa funcionalidade está disponível em breve.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'faq4'} onChange={handleAccordionChange('faq4')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">A Habitop vai ganhar IA no futuro?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Sim! Estamos trabalhando em um assistente de IA que vai analisar seus dados e dar 
                  insights personalizados sobre seus hábitos. Essa funcionalidade está em nosso roadmap 
                  e será anunciada em breve.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'faq5'} onChange={handleAccordionChange('faq5')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">O preço pode mudar no futuro?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  O preço atual é promocional de lançamento. Quem assinar agora mantém esse valor 
                  enquanto continuar com a assinatura. Novos assinantes no futuro podem ter preços 
                  diferentes, mas você que entrou agora está protegido.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Container>

        {/* CTA Final */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 6, md: 8 },
            textAlign: 'center'
          }}
        >
          <Container maxWidth="md">
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Pronto para transformar sua rotina?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
              Comece hoje e veja resultados em 7 dias
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f0f0f0',
                }
              }}
            >
              Começar agora
            </Button>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: 'background.paper', py: 4, textAlign: 'center' }}>
          <Container>
            <Typography variant="body2" color="text.secondary">
              © 2025 Habitop. Todos os direitos reservados.
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
